import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@/components/ui";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  phaseRoleOptions,
  phaseTeamOptions,
} from "@/lib/data/react-query/matters/matter-phase";
import type { PhaseAssignmentSelection } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, UserRound, Users } from "lucide-react";
import { useState } from "react";
import { PhaseTeamRoster } from "./PhaseTeamRoster";

export function PhaseAssignmentSelect({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  allowClear = true,
  allowTeam = true,
  allowUser = true,
}: {
  value: PhaseAssignmentSelection;
  onChange: (selection: PhaseAssignmentSelection) => void;
  disabled?: boolean;
  readOnly?: boolean;
  allowClear?: boolean;
  allowTeam?: boolean;
  allowUser?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"team" | "user">("team");
  const [teamSearch, setTeamSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [draft, setDraft] = useState<PhaseAssignmentSelection>({});
  const debouncedTeamSearch = useDebouncedValue(teamSearch.trim(), 250);
  const debouncedRoleSearch = useDebouncedValue(roleSearch.trim(), 250);
  const teams = useQuery({
    ...phaseTeamOptions(debouncedTeamSearch),
    enabled: open && kind === "team" && allowTeam,
  });
  const roles = useQuery({
    ...phaseRoleOptions(debouncedRoleSearch),
    enabled: open && kind === "user" && allowUser,
  });
  const directory = useUserDirectory({
    enabled: open && kind === "user" && allowUser,
    open: open && kind === "user",
    selectedIds: draft.userId ? [draft.userId] : [],
  });
  const selectedLabel = value.userId
    ? `${value.userLabel ?? "Unavailable individual"} · ${value.roleLabel ?? "Unavailable role"}${value.userAvailable === false ? " (Unavailable)" : ""}`
    : value.teamId
      ? (value.teamLabel ?? "Selected team")
      : "Unassigned";
  const current = (
    <span className="flex min-w-0 items-center gap-2">
      {value.userId ? (
        <UserRound className="size-4 shrink-0" />
      ) : (
        <Users className="size-4 shrink-0" />
      )}
      <span className="truncate">{selectedLabel}</span>
    </span>
  );
  const choose = (selection: PhaseAssignmentSelection) => {
    onChange(selection);
    setOpen(false);
  };
  if (readOnly)
    return value.teamId ? (
      <PhaseTeamRoster
        teamId={value.teamId}
        hoverTrigger={<Text variant="caption">{current}</Text>}
      />
    ) : (
      <Text variant="caption">{current}</Text>
    );
  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setKind((value.userId && allowUser) || !allowTeam ? "user" : "team");
          setDraft(value);
          setTeamSearch("");
          setRoleSearch("");
          directory.setSearch("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-label="Phase assignment"
          className="w-full max-w-sm justify-between gap-2"
        >
          {current}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] p-0"
        data-modal-popover="true"
      >
        <div
          className="flex gap-2 border-b p-2"
          role="group"
          aria-label="Assignment type"
        >
          <Button
            type="button"
            size="sm"
            variant={kind === "team" ? "default" : "ghost"}
            aria-pressed={kind === "team"}
            disabled={!allowTeam}
            onClick={() => {
              setKind("team");
            }}
          >
            Team
          </Button>
          <Button
            type="button"
            size="sm"
            variant={kind === "user" ? "default" : "ghost"}
            aria-pressed={kind === "user"}
            disabled={!allowUser}
            onClick={() => {
              setKind("user");
            }}
          >
            Individual
          </Button>
        </div>
        {kind === "team" ? (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search teams…"
              value={teamSearch}
              onValueChange={setTeamSearch}
            />
            <CommandList className="max-h-60">
              {teams.isError ? (
                <div className="p-3 text-sm">
                  Unable to load teams.{" "}
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => void teams.refetch()}
                  >
                    Try again
                  </Button>
                </div>
              ) : teams.isFetching ? (
                <div className="p-3 text-sm">Loading teams…</div>
              ) : (
                <CommandEmpty>No teams found.</CommandEmpty>
              )}
              {teams.data?.data.map((team) => (
                <CommandItem
                  key={team.id}
                  value={team.id}
                  onSelect={() => {
                    choose({
                      teamId: team.id,
                      teamLabel: team.label,
                      userId: null,
                      roleId: null,
                    });
                  }}
                >
                  <Users className="size-4" />
                  <span className="flex-1">{team.label}</span>
                  {value.teamId === team.id ? (
                    <Check className="size-4" />
                  ) : null}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        ) : (
          <div className="space-y-2">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search individuals…"
                value={directory.search}
                onValueChange={directory.setSearch}
              />
              <CommandList className="max-h-40">
                {directory.isError ? (
                  <div className="p-3 text-sm">
                    Unable to load individuals.{" "}
                    <Button
                      type="button"
                      variant="link"
                      onClick={directory.selectProps.onRetry}
                    >
                      Try again
                    </Button>
                  </div>
                ) : directory.isLoading ? (
                  <div className="p-3 text-sm">Loading individuals…</div>
                ) : (
                  <CommandEmpty>No individuals found.</CommandEmpty>
                )}
                {directory.users
                  .filter(
                    (user) =>
                      user.enabled !== false &&
                      (!directory.search.trim() ||
                        directory.selectProps.matchingValues.includes(user.id)),
                  )
                  .map((user) => {
                    const label = [
                      user.firstName,
                      user.middleName,
                      user.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <CommandItem
                        key={user.id}
                        value={user.id}
                        onSelect={() => {
                          setDraft({
                            ...draft,
                            teamId: null,
                            userId: user.id,
                            userLabel: label,
                            userAvailable: true,
                          });
                        }}
                      >
                        <UserRound className="size-4" />
                        <span className="flex-1">{label}</span>
                        {draft.userId === user.id ? (
                          <Check className="size-4" />
                        ) : null}
                      </CommandItem>
                    );
                  })}
              </CommandList>
            </Command>
            {directory.hasNextPage ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={directory.isLoading || directory.isFetchingNextPage}
                onClick={directory.selectProps.onLoadMore}
              >
                Load more individuals
              </Button>
            ) : null}
            <div className="border-t px-3 pt-2 text-sm">
              {draft.userId
                ? (draft.userLabel ?? "Unavailable individual")
                : "Select an individual"}
            </div>
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search matter roles…"
                value={roleSearch}
                onValueChange={setRoleSearch}
              />
              <CommandList className="max-h-32">
                {roles.isError ? (
                  <div className="p-3 text-sm">
                    Unable to load roles.{" "}
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => void roles.refetch()}
                    >
                      Try again
                    </Button>
                  </div>
                ) : roles.isFetching ? (
                  <div className="p-3 text-sm">Loading roles…</div>
                ) : (
                  <CommandEmpty>No roles found.</CommandEmpty>
                )}
                {roles.data?.data.map((role) => (
                  <CommandItem
                    key={role.id}
                    value={role.id}
                    onSelect={() => {
                      setDraft({
                        ...draft,
                        roleId: role.id,
                        roleLabel: role.label,
                      });
                    }}
                  >
                    <span className="flex-1">{role.label}</span>
                    {draft.roleId === role.id ? (
                      <Check className="size-4" />
                    ) : null}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
            <div className="space-y-2 px-3 pb-3">
              <Text variant="caption">
                Role: {draft.roleLabel ?? "Select a matter role"}
              </Text>
              {draft.userId && draft.userAvailable === false ? (
                <Text variant="caption">
                  This individual is unavailable. Select another individual.
                </Text>
              ) : null}
              <Button
                type="button"
                className="w-full"
                disabled={
                  !draft.userId ||
                  !draft.roleId ||
                  draft.userAvailable === false ||
                  directory.isError ||
                  roles.isError
                }
                onClick={() => {
                  choose({ ...draft, teamId: null, teamLabel: null });
                }}
              >
                Select individual and role
              </Button>
            </div>
          </div>
        )}
        {allowClear ? (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                choose({ teamId: null, userId: null, roleId: null });
              }}
            >
              Leave unassigned
            </Button>
          </div>
        ) : null}
        {value.teamId ? (
          <div className="border-t p-2">
            <PhaseTeamRoster teamId={value.teamId} />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
